import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks
export const fetchConversationList = createAsyncThunk(
  'conversations/fetchList',
  async ({ limit = 50, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await apiService.conversations.getAll({
        limit,
        offset
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchConversationById = createAsyncThunk(
  'conversations/fetchById',
  async (conversationId, { rejectWithValue }) => {
    try {
      // Fetch conversation summary and messages
      const response = await apiService.conversations.getById(conversationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'conversations/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.conversations.getUnreadCount();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markConversationAsRead = createAsyncThunk(
  'conversations/markAsRead',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await apiService.conversations.markAsRead(conversationId, {});
      return { conversationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createConversation = createAsyncThunk(
  'conversations/create',
  async (conversationData, { rejectWithValue }) => {
    try {
      const response = await apiService.conversations.create(conversationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateConversation = createAsyncThunk(
  'conversations/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.conversations.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteConversation = createAsyncThunk(
  'conversations/delete',
  async (customerId, { rejectWithValue }) => {
    try {
      await apiService.conversations.delete(customerId);
      return customerId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCustomerByPhone = createAsyncThunk(
  'conversations/fetchByPhone',
  async (phone, { rejectWithValue }) => {
    try {
      const response = await apiService.conversations.getByPhone(phone);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const exportConversation = createAsyncThunk(
  'conversations/export',
  async ({ conversationId, format = 'json' }, { rejectWithValue }) => {
    try {
      const response = await apiService.conversations.export(conversationId, { format });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  // Conversation list
  conversationList: [],
  
  // Current conversation details
  currentConversation: null,
  currentMessages: [],
  
  // Customer info for current conversation
  currentCustomer: null,
  
  // Loading states
  loading: false,
  loadingConversation: false,
  
  // Contact info for current conversation
  phoneNumberId: null,
  customerPhone: null,
  
  // Error state
  error: null,
  
  // Unread count
  unreadCount: 0,
  
  // Pagination
  pagination: {
    limit: 50,
    offset: 0,
    total: 0
  }
};

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    // Set current conversation
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload.conversation;
      state.currentCustomer = action.payload.customer;
      state.currentMessages = action.payload.messages || [];
    },
    
    // Add new message to current conversation
    addMessage: (state, action) => {
      state.currentMessages.push(action.payload);
    },
    
    // Update message status
    updateMessageStatus: (state, action) => {
      const { messageId, status } = action.payload;
      const message = state.currentMessages.find(m => m.id === messageId);
      if (message) {
        message.status = status;
      }
    },
    
    // Clear current conversation
    clearCurrentConversation: (state) => {
      state.currentConversation = null;
      state.currentMessages = [];
      state.currentCustomer = null;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Update pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    }
  },
  
  extraReducers: (builder) => {
    // Create conversation
    builder
      .addCase(createConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.conversationList.unshift(action.payload);
      })
      .addCase(createConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update conversation
    builder
      .addCase(updateConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConversation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.conversationList.findIndex(
          c => c.id === action.payload.id || c.customerId === action.payload.customerId
        );
        if (index !== -1) {
          state.conversationList[index] = action.payload;
        }
        if (state.currentConversation?.id === action.payload.id) {
          state.currentConversation = action.payload;
        }
      })
      .addCase(updateConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch conversation list
    builder
      .addCase(fetchConversationList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversationList.fulfilled, (state, action) => {
        state.loading = false;
        state.conversationList = action.payload.data || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchConversationList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch conversation by ID
    builder
      .addCase(fetchConversationById.pending, (state) => {
        state.loadingConversation = true;
        state.error = null;
      })
      .addCase(fetchConversationById.fulfilled, (state, action) => {
        state.loadingConversation = false;
        // Handle nested data structure (data.data)
        const payload = action.payload.data || action.payload;
        state.currentConversation = payload;
        // Extract messages from the payload
        if (payload.messages && Array.isArray(payload.messages)) {
          state.currentMessages = payload.messages;
        } else if (action.payload.messages && Array.isArray(action.payload.messages)) {
          state.currentMessages = action.payload.messages;
        } else {
          state.currentMessages = [];
        }
        // Store phone number info for sending messages
        if (payload.phoneNumberId) {
          state.phoneNumberId = payload.phoneNumberId;
        }
        if (payload.customerPhone) {
          state.customerPhone = payload.customerPhone;
        }
      })
      .addCase(fetchConversationById.rejected, (state, action) => {
        state.loadingConversation = false;
        state.error = action.payload;
      });

    // Fetch unread count
    builder
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unreadCount || 0;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        console.error('Error fetching unread count:', action.payload);
      });

    // Mark conversation as read
    builder
      .addCase(markConversationAsRead.fulfilled, (state, action) => {
        state.unreadCount = Math.max(0, state.unreadCount - (action.payload.updatedCount || 0));
      })
      .addCase(markConversationAsRead.rejected, (state, action) => {
        console.error('Error marking as read:', action.payload);
      });

    // Delete conversation
    builder
      .addCase(deleteConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.conversationList = state.conversationList.filter(
          c => c.id !== action.payload && c.customerId !== action.payload
        );
        if (state.currentConversation?.id === action.payload) {
          state.currentConversation = null;
          state.currentMessages = [];
        }
      })
      .addCase(deleteConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Export conversation
    builder
      .addCase(exportConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportConversation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setCurrentConversation,
  addMessage,
  updateMessageStatus,
  clearCurrentConversation,
  clearError,
  setPagination
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
